import { IconButton, Menu, MenuItem } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { useAuth } from "@bw/shared/hooks";
import type { PostResponse } from "@bw/shared/types/generated";
import { useRef, useState } from "react";
import { EditPostDialog } from "./EditPostDialog";
import { ReportDialog } from "../../../common/components/ReportDialog";

export function PostMenu({ post }: { post: PostResponse }) {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [editing, setEditing] = useState(false);
  const [reporting, setReporting] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  if (!user) return null;

  const isOwnPost = user.uid === post.author_id;

  const open = Boolean(anchorEl);
  return (
    <>
      <IconButton
        ref={triggerRef}
        size="small"
        aria-label="Post options"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <MoreHorizRoundedIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          list: { "aria-label": "Post options" },
          backdrop: {
            "data-testid": "post-options-backdrop",
          } as Record<string, string>,
        }}
      >
        {isOwnPost ? (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setEditing(true);
            }}
          >
            <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            Edit post
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setReporting(true);
            }}
          >
            <FlagOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            Report post
          </MenuItem>
        )}
      </Menu>
      {editing ? (
        <EditPostDialog
          post={post}
          onClose={() => {
            setEditing(false);
            requestAnimationFrame(() => triggerRef.current?.focus());
          }}
        />
      ) : null}
      {!isOwnPost ? (
        <ReportDialog
          open={reporting}
          subjectLabel="post"
          targetId={post.post_id}
          targetType="post"
          onClose={() => {
            setReporting(false);
            requestAnimationFrame(() => triggerRef.current?.focus());
          }}
        />
      ) : null}
    </>
  );
}
